import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Logo from './Logo';
import { useCart } from '../context/CartContext';

const NAV: { label: string; to: string }[] = [
  { label: 'Shop All', to: '/shop' },
  { label: "Women's", to: '/shop?gender=women' },
  { label: "Men's", to: '/shop?gender=men' },
  { label: 'Brands', to: '/brands' },
  { label: 'How It Works', to: '/how-it-works' },
  { label: 'Sell', to: '/sell' },
  { label: 'About', to: '/about' },
  { label: 'FAQ', to: '/faq' },
];

export default function Header() {
  const cart = useCart();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.search]);

  const search = (e: React.FormEvent) => {
    e.preventDefault();
    navigate(q.trim() ? `/shop?q=${encodeURIComponent(q.trim())}` : '/shop');
  };

  const hasCart = cart.count > 0;

  return (
    <header className="sticky top-0 z-40 border-b border-clay/40 bg-bone backdrop-blur-xl bg-bone/95">
      <div className="container-editorial flex items-center justify-between gap-4 py-3">
        <div className="flex items-center gap-3">
          <button
            type="button"
            aria-label="Open menu"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="grid h-10 w-10 place-items-center rounded-xl border border-clay text-ink transition hover:bg-sand lg:hidden"
          >
            <MenuIcon open={open} />
          </button>
          <Logo />
        </div>

        <nav className="hidden items-center gap-5 lg:flex">
          {NAV.map((item) => (
            <NavLink
              key={item.label}
              to={item.to}
              className={({ isActive }) =>
                `whitespace-nowrap text-[14px] font-medium transition-colors duration-200 hover:text-ink ${isActive ? 'text-ink' : 'text-slate'}`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <form onSubmit={search} className="hidden items-center lg:flex">
            <div className="relative">
              <SearchIcon />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search"
                aria-label="Search inventory"
                className="h-9 w-36 rounded-xl border border-clay/60 bg-sand/50 pl-8 pr-3 text-[13px] text-ink outline-none transition placeholder:text-stone/60 focus:border-plug focus:bg-linen focus:ring-2 focus:ring-plug/12 xl:w-48"
              />
            </div>
          </form>

          <button
            type="button"
            onClick={() => cart.setOpen(true)}
            className="relative grid h-9 w-9 place-items-center rounded-xl border border-clay text-ink transition hover:bg-sand"
            aria-label={`Cart (${cart.count})`}
          >
            <BagIcon />
            {hasCart && (
              <span className="absolute -right-1.5 -top-1.5 grid h-5 min-w-[20px] place-items-center rounded-full bg-plug px-1.5 text-[11px] font-bold text-white shadow-sm animate-scale-in">
                {cart.count}
              </span>
            )}
          </button>

          <Link to={hasCart ? '/cart' : '/shop'} className="btn-primary btn-sm whitespace-nowrap">
            {hasCart ? 'View Cart' : 'Shop Now'}
          </Link>
        </div>
      </div>

      <div
        className={`fixed inset-x-0 top-[var(--header-h,72px)] z-30 origin-top border-b border-clay/60 bg-bone transition-all duration-300 lg:hidden ${
          open ? 'pointer-events-auto translate-y-0 opacity-100' : 'pointer-events-none -translate-y-3 opacity-0'
        }`}
      >
        <div className="container-editorial flex flex-col py-4 max-h-[70vh] overflow-y-auto">
          <form onSubmit={search} className="mb-3">
            <div className="relative">
              <SearchIcon />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search"
                aria-label="Search inventory"
                className="field h-11 pl-9"
              />
            </div>
          </form>

          {NAV.map((item) => (
            <Link
              key={item.label}
              to={item.to}
              onClick={() => setOpen(false)}
              className="rounded-xl px-3 py-3 text-[16px] font-medium text-ink hover:bg-sand transition-colors"
            >
              {item.label}
            </Link>
          ))}
          <Link
            to="/contact"
            onClick={() => setOpen(false)}
            className="rounded-xl px-3 py-3 text-[16px] font-medium text-slate hover:bg-sand transition-colors"
          >
            Contact
          </Link>
          <Link
            to="/alerts"
            onClick={() => setOpen(false)}
            className="rounded-xl px-3 py-3 text-[16px] font-medium text-slate hover:bg-sand transition-colors"
          >
            Size Alerts
          </Link>
        </div>
      </div>
      {open && (
        <button type="button" aria-label="Close menu" onClick={() => setOpen(false)} className="fixed inset-0 z-20 bg-black/30 lg:hidden" />
      )}
    </header>
  );
}

function MenuIcon({ open }: { open: boolean }) {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden>
      <path d={open ? 'M3 3l12 12M15 3L3 15' : 'M2 5h14M2 9h14M2 13h14'} stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}
function BagIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden>
      <path d="M4 6h10l-.7 8.5a1.5 1.5 0 0 1-1.5 1.4H6.2a1.5 1.5 0 0 1-1.5-1.4L4 6Z" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <path d="M6.5 6V4.7a2.5 2.5 0 1 1 5 0V6" fill="none" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  );
}
function SearchIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 18 18" aria-hidden className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-stone/40">
      <circle cx="8" cy="8" r="5" stroke="currentColor" strokeWidth="1.5" fill="none" />
      <path d="M12 12l4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}
